import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { getCollection } from '@/lib/firebase';
import { Calendar, Users, FileText, Briefcase, Clock, AlertCircle, Settings } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Appointment {
  id: string;
  patientName: string;
  date: string;
  time: string;
  service: string;
  status: string;
}

interface Stats {
  appointments: number;
  patients: number;
  blogPosts: number;
  cases: number;
}

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats>({
    appointments: 0,
    patients: 0,
    blogPosts: 0,
    cases: 0
  });
  const [recentAppointments, setRecentAppointments] = useState<Appointment[]>([]);
  const [totalClinicAppointments, setTotalClinicAppointments] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all data in parallel
        const [appointmentsRes, patientsRes, blogPostsRes, casesRes, clinicAppointmentsRes] = await Promise.all([
          getCollection('bookings'),
          getCollection('patients'),
          getCollection('blogPosts'),
          getCollection('cases'),
          getCollection('clinicAppointments')
        ]);

        // Update stats
        setStats({
          appointments: appointmentsRes.data?.length || 0,
          patients: patientsRes.data?.length || 0,
          blogPosts: blogPostsRes.data?.length || 0,
          cases: casesRes.data?.length || 0
        });

        // إجمالي الكشوفات من clinicAppointments
        setTotalClinicAppointments(clinicAppointmentsRes.data?.length || 0);

        // Sort and set recent appointments from clinicAppointments
        if (clinicAppointmentsRes.data) {
          const sortedAppointments = [...clinicAppointmentsRes.data]
            .sort((a, b) => {
              const aDate = a.createdAt && a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
              const bDate = b.createdAt && b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
              return bDate.getTime() - aDate.getTime();
            })
            .slice(0, 5)
            .map(app => ({
              id: app.id,
              patientName: app.patientName,
              service: app.serviceType,
              date: app.createdAt && app.createdAt.toDate ? app.createdAt.toDate().toLocaleDateString('ar-EG') : new Date(app.createdAt).toLocaleDateString('ar-EG'),
              time: app.time || '',
              status: app.queueStatus || ''
            }));
          setRecentAppointments(sortedAppointments);
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('حدث خطأ أثناء تحميل البيانات');
        toast({
          variant: 'destructive',
          title: 'خطأ في تحميل البيانات',
          description: 'حدث خطأ أثناء تحميل بيانات لوحة التحكم',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [toast]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-lg text-gray-600">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <AlertCircle className="w-12 h-12 text-red-600" />
          <p className="text-lg text-gray-600">{error}</p>
          <Button onClick={() => window.location.reload()}>إعادة المحاولة</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">لوحة التحكم</h1>
        <Button onClick={() => navigate('/admin/settings')} className="flex items-center gap-2">
          <Settings className="w-5 h-5" />
          إعدادات العيادة
        </Button>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/clinic-appointments')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">كشوفات العيادة</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalClinicAppointments}</div>
            <p className="text-xs text-muted-foreground">إجمالي الكشوفات</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/appointments')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">حجوزات الموقع</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.appointments}</div>
            <p className="text-xs text-muted-foreground">حجوزات جديدة</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/cases')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">الحالات</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.cases}</div>
            <p className="text-xs text-muted-foreground">الحالات المسجلة</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/blog')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">المدونة</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.blogPosts}</div>
            <p className="text-xs text-muted-foreground">مقالات المدونة</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Appointments */}
      {recentAppointments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              المواعيد الأخيرة
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentAppointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <h3 className="font-medium">{appointment.patientName}</h3>
                    <p className="text-sm text-gray-500">{appointment.service}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{appointment.date}</p>
                    <p className="text-sm text-gray-500">{appointment.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;